import React from "react";
import ReactDOM from "react-dom/client";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import "./index.css";

// If a lazy-loaded chunk fails to fetch (e.g. stale index.html after a new
// deploy replaced the hashed asset files), force a one-time hard reload so
// the user gets the current build instead of a broken page.
const CHUNK_RELOAD_KEY = "chunk-reload-attempted";
window.addEventListener("vite:preloadError", () => {
  if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
  }
});
window.addEventListener("unhandledrejection", (event) => {
  const message = String(event.reason?.message ?? "");
  if (
    /Failed to fetch dynamically imported module|Importing a module script failed/i.test(
      message,
    ) &&
    !sessionStorage.getItem(CHUNK_RELOAD_KEY)
  ) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    window.location.reload();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
  // General mutation-complete signifier: toasts by default for every
  // mutation (`meta.successMessage` overrides the generic "Saved." text).
  // Opt out per-mutation with `meta: { suppressToast: true }` for mutations
  // that already give their own feedback or fire too often/silently to be
  // toast-worthy. Surfaces via the same `ui:toast` event Layout.tsx renders.
  mutationCache: new MutationCache({
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.meta?.suppressToast) return;
      const message =
        typeof mutation.meta?.successMessage === "string"
          ? mutation.meta.successMessage
          : "Saved.";
      window.dispatchEvent(
        new CustomEvent("ui:toast", { detail: { message } }),
      );
    },
  }),
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
sessionStorage.removeItem(CHUNK_RELOAD_KEY);
